export const getCheckSumCharacter = (code: string): string => {
    const chars = code.split('');

    /* eslint-disable quote-props */
    const oddCharsMap: Record<string, number> = {
        '0': 1, '9': 21, 'I': 19, 'R': 8,
        '1': 0, 'A': 1, 'J': 21, 'S': 12,
        '2': 5, 'B': 0, 'K': 2, 'T': 14,
        '3': 7, 'C': 5, 'L': 4, 'U': 16,
        '4': 9, 'D': 7, 'M': 18, 'V': 10,
        '5': 13, 'E': 9, 'N': 20, 'W': 22,
        '6': 15, 'F': 13, 'O': 11, 'X': 25,
        '7': 17, 'G': 15, 'P': 3, 'Y': 24,
        '8': 19, 'H': 17, 'Q': 6, 'Z': 23
    };
    const evenCharsMap: Record<string, number> = {
        '0': 0, '9': 9, 'I': 8, 'R': 17,
        '1': 1, 'A': 0, 'J': 9, 'S': 18,
        '2': 2, 'B': 1, 'K': 10, 'T': 19,
        '3': 3, 'C': 2, 'L': 11, 'U': 20,
        '4': 4, 'D': 3, 'M': 12, 'V': 21,
        '5': 5, 'E': 4, 'N': 13, 'W': 22,
        '6': 6, 'F': 5, 'O': 14, 'X': 23,
        '7': 7, 'G': 6, 'P': 15, 'Y': 24,
        '8': 8, 'H': 7, 'Q': 16, 'Z': 25
    };
    const remainderMap = [
        'A', 'B', 'C', 'D', 'E', 'F', 'G',
        'H', 'I', 'J', 'K', 'L', 'M', 'N',
        'O', 'P', 'Q', 'R', 'S', 'T', 'U',
        'V', 'W', 'X', 'Y', 'Z'
    ];
    /* eslint-disable quote-props */

    const sum = chars.reduce((acc, c, index) => {
        const charMaps = (index + 1) % 2 === 0 ? evenCharsMap : oddCharsMap;

        return acc + (charMaps[c] || 0);
    }, 0);
    const remainder = sum % 26;

    return remainderMap[remainder];
};

export const getName3LetterCode = (name: string, shouldSkipSecond = false): string => {
    const vowels = ['A', 'E', 'I', 'O', 'U'];

    const chars = name
        .replace(/[^a-z0-9]/gi, '')
        .toUpperCase()
        .split('');

    const nameVowels = chars.filter(c => vowels.includes(c));
    let nameConsonants = chars.filter(c => !vowels.includes(c));

    if (shouldSkipSecond && nameConsonants.length > 3) {
        const [c1, , ...otherNameConsonants] = nameConsonants;

        nameConsonants = [c1, ...otherNameConsonants];
    }

    return [
        ...nameConsonants,
        ...nameVowels,
        ...['X', 'X', 'X']
    ].slice(0, 3).join('');
};

/**
 * Get month letter code mapped like:
 * A - January; B - February; C - March;
 * D - April;   E - May;      H - June;
 * L - July;    M - August;   P - September;
 * R - October; S - November; T - December
 * @param mm {string} Month number from "01" to "12"
 * @return {string}
 */
export const getMonthLetter = (mm: string): string => {
    const map = ['', 'A', 'B', 'C', 'D', 'E', 'H', 'L', 'M', 'P', 'R', 'S', 'T'];
    const monthNmb = Number(mm);

    return map[monthNmb];
};

/**
 * Birthday and gender (2 digits): the two birthday digits are used (from 01 to 31);
 * if the person is a woman, 40 is added (e.g. 01 would be 41, 31 would be 71).
 * @param dd {string} Day from "01" to "31"
 * @param gender {string} "M" or "F"
 * @return {string} from "01" to "31" for Male and "41" to "71" for Female
 */
export const getBirthDayWithGenderCode = (dd: string, gender: string): string => {
    if (gender === 'M' || !dd) {
        return dd;
    }

    return (Number(dd) + 40).toString();
};

export const italianIdDigitToLetter = (digit: string): string => {
    const digitsMap = ['L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'U', 'V'];

    return digitsMap[parseInt(digit)];
};

export const italianIdLetterToDigit = (char: string): string => {
    const isDigit = (c: string): boolean => !Number.isNaN(parseInt(c));
    const digitsMap = ['L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'U', 'V'];

    return char
        .split('')
        .map(c => isDigit(c) ? Number(c) : digitsMap.indexOf(c))
        .filter(d => d !== -1)
        .map(d => d.toString())
        .join('');
};

/**
 * Compare 2 fiscal codes with taking into account that in cases when two people yield the same code,
 * the fiscal administration replaces some numeric digits (starting from the rightmost one)
 * with letters:
 * | Digit | Letter | Digit | Letter |
 * |   0   |   L    |   5   |   R    |
 * |   1   |   M    |   6   |   S    |
 * |   2   |   N    |   7   |   T    |
 * |   3   |   P    |   8   |   U    |
 * |   4   |   Q    |   9   |   V    |
 * @param code1 {string} 15 character fiscal code
 * @param code2 {string} 15 character fiscal code
 * @return {number[]} indexes where IDs are different
 */
export const compareItalianIDs = (code1: string, code2: string): number[] => {
    if (code1?.length !== code2?.length) {
        return [15];
    }

    let diffIndexes: number[] = [];
    const isDigit = (c: string): boolean => !Number.isNaN(parseInt(c));
    const code1Chars = code1.split('');
    const code2Chars = code2.split('');

    code1Chars
        .map((c1, index) => [c1, code2Chars[index]])
        .forEach(([c1, c2], index) => {
            if (c1 === c2) {
                return;
            }

            if (isDigit(c1) && !isDigit(c2)) {
                diffIndexes = [...diffIndexes, ...italianIdDigitToLetter(c1) !== c2 && [index] || []];
            } else if (!isDigit(c1) && isDigit(c2)) {
                diffIndexes = [...diffIndexes, ...c1 !== italianIdDigitToLetter(c2) && [index] || []];
            } else {
                diffIndexes = [...diffIndexes, index];
            }
        });

    return diffIndexes;
};
